import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { SrOrderStatus } from '@plato/data/starrocks/types';
import { usePersistedQueryParams } from '../../../lib/hooks/use-persisted-query-params.ts';
import type { SetValue } from '../../../lib/hooks/use-query-params.ts';
import {
    dateParam,
    endOfDayParam,
    enumParam,
    generateQueryParamsString,
    numberParam,
    objectParam,
    queryParamsConfig,
    stringArrayParam,
    stringParam,
} from '../../../lib/query-params.ts';
import type { RouterInput } from '../../../server/types/infer-trpc-types.ts';
import { QuotesOrdersSortBy, SortOrder } from '../../../server/types/list-view-types.ts';
import type { EntryDateRange } from '../../filters/types/filter-types.ts';
import { DateRangeComparison, RelativeTimeUnit } from '../../filters/types/filter-types.ts';

export type QuoteOrderQueryFilters = Pick<
    RouterInput['platoUser']['getManyQuotesOrders']['getWithFilters'],
    | 'salesRepIds'
    | 'clerkIds'
    | 'storageLocationIds'
    | 'companyIds'
    | 'documentTypes'
    | 'searchText'
    | 'totalAmountNet'
    | 'totalMargin'
    | 'entryDateRange'
> & {
    orderStatus?: string[];
    sortingFilter: NonNullable<{
        field: QuotesOrdersSortBy;
        direction: SortOrder;
    }>;
};
const config = queryParamsConfig<QuoteOrderQueryFilters>({
    orderStatus: stringArrayParam(),
    salesRepIds: stringArrayParam(),
    clerkIds: stringArrayParam(),
    storageLocationIds: stringArrayParam(),
    companyIds: stringArrayParam(),
    documentTypes: stringArrayParam(),
    searchText: stringParam(),
    sortingFilter: objectParam({
        field: enumParam(QuotesOrdersSortBy),
        direction: enumParam(SortOrder),
    }),
    entryDateRange: objectParam<NonNullable<EntryDateRange>>({
        from: dateParam(),
        to: endOfDayParam(),
        unit: enumParam(RelativeTimeUnit),
        count: numberParam(),
        comparison: enumParam(DateRangeComparison),
    }),
    // objectParam here returns a parser expecting required properties internally — cast to the
    // expected shape with optional min/max to satisfy the QueryParamsObjectConfig typing.
    totalAmountNet: objectParam<{ min?: number; max?: number }>({
        min: numberParam(),
        max: numberParam(),
    }),
    totalMargin: objectParam<{ min?: number; max?: number }>({
        min: numberParam(),
        max: numberParam(),
    }),
});

export const openOrdersFilter = {
    orderStatus: [SrOrderStatus.OPEN, SrOrderStatus.OPEN_IN_PICKING] as string[],
    storageLocationIds: [],
    companyIds: [],
    documentTypes: [],
    clerkIds: [],
    searchText: '',
    salesRepIds: [],
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    },
} satisfies QuoteOrderQueryFilters;

export const openQuotesFilter = {
    orderStatus: [SrOrderStatus.OPEN] as string[],
    storageLocationIds: [],
    companyIds: [],
    documentTypes: [],
    clerkIds: [],
    searchText: '',
    salesRepIds: [],
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    },
} satisfies QuoteOrderQueryFilters;

export const billedOrdersFilter = {
    orderStatus: [SrOrderStatus.BILLED] as string[],
    storageLocationIds: [],
    companyIds: [],
    documentTypes: [],
    clerkIds: [],
    searchText: '',
    salesRepIds: [],
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    },
} satisfies QuoteOrderQueryFilters;

export const defaultQuoteOrderFilters = {
    salesRepIds: [] as string[],
    clerkIds: [] as string[],
    orderStatus: [] as string[],
    storageLocationIds: [] as string[],
    companyIds: [] as string[],
    documentTypes: [] as string[],
    searchText: '',
    sortingFilter: {
        field: QuotesOrdersSortBy.ORDER_CREATED_AT_DATE,
        direction: SortOrder.DESC,
    } as {
        field: QuotesOrdersSortBy;
        direction: SortOrder;
    },
} satisfies QuoteOrderQueryFilters;

export const generateSearchParamsForQuotesOrders = (params: Partial<QuoteOrderQueryFilters>): string =>
    generateQueryParamsString(config, params);

export const useQuoteOrderListFilters = (
    orderType: SrSalesOrderType,
): [QuoteOrderQueryFilters, SetValue<QuoteOrderQueryFilters>] => {
    return usePersistedQueryParams(config, defaultQuoteOrderFilters, orderType);
};
